import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { streamAPI } from '../services/api';
import adminFetch from '../services/adminFetch';
import { getImageUrl, getVideoUrl } from '../config';
import { addTouchOptimization, getDeviceType } from '../utils/responsive';
import SidebarNav from './common/SidebarNav';

const HomePage = () => {
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [videoReadyMap, setVideoReadyMap] = useState({});
  const navigate = useNavigate();
  const isRequesting = useRef(false);

  const fetchStreams = async () => {
    if (isRequesting.current) return;
    isRequesting.current = true;
    setLoading(true);
    setError(null);

    try {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('请求超时，请检查网络连接')), 30000);
      });

      const response = await Promise.race([
        streamAPI.getStreams(true, false),
        timeoutPromise
      ]);

      if (response?.data) {
        setStreams(response.data);
      } else {
        throw new Error('数据格式错误');
      }
    } catch (err) {
      setError('获取数据失败: ' + err.message);
      setStreams([]);
    } finally {
      setLoading(false);
      isRequesting.current = false;
    }
  };

  useEffect(() => {
    fetchStreams();
  }, []);

  const handleStreamClick = (streamId) => navigate(`/stream/${streamId}`);

  const [creating, setCreating] = useState(false);
  const handleCreateClick = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const formData = new FormData();
      formData.append('name', '新互动空间');
      const res = await adminFetch('/admin/create-empty-stream', { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '创建失败');
      navigate(`/admin/stream/${data.stream_id}`);
    } catch (err) {
      alert('创建失败: ' + err.message);
      setCreating(false);
    }
  };

  const handleVideoMouseEnter = (e) => {
    if (!e.target.paused) e.target.pause();
  };

  const handleVideoMouseLeave = (e) => {
    if (e.target.paused) e.target.play().catch(() => {});
  };

  const setVideoReady = (streamId, ready) => {
    setVideoReadyMap((prev) => {
      if (prev[streamId] === ready) return prev;
      return { ...prev, [streamId]: ready };
    });
  };

  useEffect(() => {
    if (getDeviceType() === 'mobile') {
      document.querySelectorAll('.stream-card, .create-card').forEach(addTouchOptimization);
    }
  }, [streams]);

  if (loading) {
    return (
      <div className="App">
        <SidebarNav />
        <div className="main-layout">
          <div className="content-area">
            <div className="container">
              <div className="loading">
                <div className="loading-spinner" />
                <h2>正在加载互动空间...</h2>
                <p>请稍候，我们正在为您准备精彩内容</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <SidebarNav />
      <div className="main-layout">
        <div className="content-area">
          <div className="container">
            {error && (
              <div className="error">
                <div style={{ marginBottom: '10px' }}>{error}</div>
                <button className="btn btn-primary" onClick={fetchStreams}>🔄 重新加载</button>
              </div>
            )}

            <div className="grid grid-2">
              {streams.map((stream) => (
                <div
                  key={stream.id}
                  className="stream-card"
                  onClick={() => handleStreamClick(stream.id)}
                  style={{
                    position: 'relative',
                    overflow: 'hidden',
                    opacity: stream.is_active ? 1 : 0.7,
                    filter: stream.is_active ? 'none' : 'grayscale(30%)'
                  }}
                >
                  {stream.default_video ? (
                    <div className="video-shell">
                      {(stream.default_video.first_frame_url
                        || stream.initial_image_url
                        || stream.character_image_url) && !videoReadyMap[stream.id] && (
                        <img
                          className="video-poster-layer"
                          src={
                            stream.default_video.first_frame_url
                            || stream.initial_image_url
                            || stream.character_image_url
                          }
                          alt={`${stream.name} 首帧图`}
                        />
                      )}
                      <video
                        className="stream-background-video"
                        autoPlay
                        muted
                        loop
                        playsInline
                        poster={
                          stream.default_video.first_frame_url
                          || stream.initial_image_url
                          || stream.character_image_url
                          || undefined
                        }
                        style={{
                          position: 'absolute',
                          top: 0, left: 0,
                          width: '100%', height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'center',
                          zIndex: 1,
                          opacity: videoReadyMap[stream.id] ? 1 : 0,
                          transition: 'opacity 0.2s ease'
                        }}
                        onLoadedData={() => setVideoReady(stream.id, true)}
                        onCanPlay={() => setVideoReady(stream.id, true)}
                        onWaiting={() => setVideoReady(stream.id, false)}
                        onError={(e) => {
                          setVideoReady(stream.id, false);
                          e.target.style.display = 'none';
                        }}
                        onMouseEnter={handleVideoMouseEnter}
                        onMouseLeave={handleVideoMouseLeave}
                      >
                        <source src={stream.default_video.video_url || getVideoUrl(stream.default_video.filename)} type="video/mp4" />
                      </video>
                    </div>
                  ) : (
                    <div
                      className="stream-background-image"
                      style={{
                        position: 'absolute',
                        top: 0, left: 0,
                        width: '100%', height: '100%',
                        backgroundImage: stream.character_image
                          ? `url(${stream.character_image_url || getImageUrl(stream.character_image)})`
                          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        zIndex: 1
                      }}
                    />
                  )}

                  {stream.is_active && stream.default_video ? (
                    <div className="live-badge" style={{ zIndex: 3 }}>互动中</div>
                  ) : stream.is_active && !stream.default_video ? (
                    <div className="live-badge" style={{ backgroundColor: '#ffc107', opacity: 0.9, zIndex: 3 }}>休息中</div>
                  ) : (
                    <div className="live-badge" style={{ backgroundColor: '#6c757d', opacity: 0.9, zIndex: 3 }}>准备中</div>
                  )}

                  <button
                    className="stream-admin-btn"
                    onClick={(e) => { e.stopPropagation(); navigate(`/admin/stream/${stream.id}`); }}
                    title="管理互动空间"
                  >
                    ⚙️
                  </button>

                  <div className="stream-content-overlay" style={{ zIndex: 2 }}>
                    <div className="stream-info">
                      <div className="stream-title">{stream.name}</div>
                      <div className="stream-description">{stream.description || '暂无描述'}</div>
                    </div>
                    <div className="stream-meta">
                      <span>{new Date(stream.created_at).toLocaleDateString('zh-CN', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                      <span className={`status ${stream.is_active && stream.default_video ? 'active' : stream.is_active && !stream.default_video ? 'resting' : 'inactive'}`}>
                        {stream.is_active && stream.default_video ? '● 互动中' : stream.is_active && !stream.default_video ? '● 休息中' : '○ 准备中'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              <div className={`create-card ${creating ? 'creating' : ''}`} onClick={handleCreateClick}>
                <div className="create-icon">{creating ? '...' : '+'}</div>
                <div className="create-title">{creating ? '创建中...' : '创建新空间'}</div>
                <div className="create-description">创建空白空间，在管理页配置内容</div>
              </div>
            </div>

            {streams.length === 0 && !loading && (
              <div className="empty-state">
                <div className="empty-icon">🎬</div>
                <h3>还没有互动空间</h3>
                <p>创建您的第一个AI互动空间，开启精彩的互动体验！</p>
                <button className="create-first-btn" onClick={handleCreateClick} disabled={creating}>
                  {creating ? '创建中...' : '立即创建互动空间'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
